import { useEffect, useState } from "react";

export default function HollywoodHeroSection() {
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  return (
    <>
      {/* Nav */}
      <nav
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          scrolled ? "bg-white shadow-sm" : "bg-transparent"
        }`}
      >
        <div className="max-w-7xl mx-auto px-4 md:px-8 flex items-center justify-between h-16 md:h-20">
          <a href="/" className="flex items-center gap-2">
            <img
              src="https://staging.fastpassportcenter.com/wp-content/uploads/2026/02/Fast-Passport-Center.png"
              alt="Fast Passport Center"
              className="h-8 md:h-9 w-auto"
            />
          </a>
          <div className="hidden md:flex items-center gap-8">
            {[
              { label: "Services", href: "#services" },
              { label: "How It Works", href: "#how-it-works" },
              { label: "FAQ", href: "#faq" },
              { label: "Resources", href: "#local-resources" },
            ].map((link) => (
              <a
                key={link.href}
                href={link.href}
                className={`text-sm font-semibold transition-colors ${
                  scrolled
                    ? "text-[#1A365D] hover:text-[#F6C344]"
                    : "text-white hover:text-[#F6C344]"
                }`}
              >
                {link.label}
              </a>
            ))}
          </div>
          <a
            href="https://fastpassportcenter.com/checkout/"
            className="bg-[#F6C344] hover:bg-[#e5b430] text-[#1A365D] font-bold text-sm px-5 py-2.5 rounded-md transition-all whitespace-nowrap"
          >
            Start My Passport
          </a>
        </div>
      </nav>

      {/* Hero */}
      <section className="relative h-[500px] md:h-[640px] flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://readdy.ai/api/search-image?query=Aerial%20view%20of%20a%20beautiful%20South%20Florida%20beachfront%20promenade%20with%20turquoise%20ocean%20waters%2C%20palm%20trees%2C%20white%20sand%20beach%2C%20colorful%20beachfront%20buildings%2C%20sunny%20day%20with%20clear%20sky%2C%20Hollywood%20Florida%20Broadwalk%20style%20coastal%20cityscape%2C%20warm%20tropical%20atmosphere%2C%20photorealistic%20landscape%20photography&width=1600&height=700&seq=hollywoodhero&orientation=landscape"
            alt="Hollywood Beach Broadwalk and oceanfront"
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/25 to-black/45"></div>
        </div>

        <div className="relative z-10 text-center max-w-4xl mx-auto px-4 md:px-8 pt-16">
          <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-4">
            Hollywood, FL — Expedited Passport Service
          </p>
          <h1 className="font-serif text-3xl md:text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight">
            Get Your Passport Fast in Hollywood, FL — <span className="text-[#F6C344]">Same-Day Processing</span> Available
          </h1>
          <p className="text-white/80 text-base md:text-lg max-w-2xl mx-auto mb-8 leading-relaxed">
            Hollywood Beach to Port Everglades to FLL — travelers across Broward County trust Fast Passport Center for emergency and expedited passports. As fast as 24 hours. Start online, ship overnight, and skip the Miami agency lines.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <a
              href="https://fastpassportcenter.com/checkout/"
              className="inline-flex items-center gap-2 bg-[#F6C344] hover:bg-[#e5b430] text-[#1A365D] font-bold text-base px-8 py-4 rounded-md transition-all hover:scale-105 whitespace-nowrap"
            >
              Start My Passport <i className="ri-arrow-right-line"></i>
            </a>
            <a
              href="tel:8772530084"
              className="inline-flex items-center gap-2 bg-white/10 hover:bg-white/20 backdrop-blur-sm text-white font-semibold text-base px-6 py-4 rounded-md transition-all whitespace-nowrap"
            >
              <i className="ri-phone-line"></i> 877-253-0084
            </a>
          </div>

          {/* Trust signals */}
          <div className="mt-10 flex flex-wrap items-center justify-center gap-5 md:gap-8">
            <div className="flex items-center gap-2 text-white/70 text-sm">
              <div className="flex items-center gap-0.5">
                <i className="ri-star-fill text-[#F6C344] text-sm"></i>
                <i className="ri-star-fill text-[#F6C344] text-sm"></i>
                <i className="ri-star-fill text-[#F6C344] text-sm"></i>
                <i className="ri-star-fill text-[#F6C344] text-sm"></i>
                <i className="ri-star-fill text-[#F6C344] text-sm"></i>
              </div>
              <span className="font-semibold">5.0</span>
              <span>Reviews</span>
            </div>
            <div className="flex items-center gap-2 text-white/70 text-sm">
              <i className="ri-award-line text-[#F6C344] text-base"></i>
              <span>BBB A+</span>
              <span>Accredited</span>
            </div>
            <div className="flex items-center gap-2 text-white/70 text-sm">
              <i className="ri-calendar-check-line text-[#F6C344] text-base"></i>
              <span>20+ Years</span>
            </div>
            <div className="flex items-center gap-2 text-white/70 text-sm">
              <i className="ri-shield-check-line text-[#F6C344] text-base"></i>
              <span>Registered State Dept. Courier</span>
            </div>
            <div className="flex items-center gap-2 text-white/70 text-sm">
              <i className="ri-price-tag-3-line text-[#F6C344] text-base"></i>
              <span>Best Price Guarantee</span>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}